<script lang="ts" setup>
import { computed, nextTick, watch } from 'vue';
import StackedServiceCard from './StackedServiceCard.vue';
import { useStackedWorkflow } from '../composables/useStackedWorkflow';
import { useHierarchyLines, type LineConnection } from '../composables/useHierarchyLines';
import type { Service, ServiceRunStatus } from '@/types';

const props = defineProps<{
  nodes: any[];
  edges: any[];
  canvasMode: 'editor' | 'history';
  runningNodes?: Set<string>;
  liveRunData?: Map<string, any>;
  /** History mode: per-service effective status (raw + inferred running frontier), shared with the
   *  canvas so both views animate the same "what's running now" segment. */
  effectiveStatusByService?: Map<string, ServiceRunStatus>;
  /** History mode: whether lines should carry status colour now — true through the run and the brief
   *  post-finish hold, then false so lines fade to grey. Shared with the canvas so both release together. */
  runColoringActive?: boolean;
  /** Resolved services (with any dirty edits overlaid). Required for editor mode. */
  services?: Service[];
  /** Set of service ids that currently have unsaved edits. */
  dirtyServiceIds?: Set<string>;
  /** Reserve extra right padding so service cards don't sit under the absolutely-
   *  positioned zoom controls (used in the builder, not in the project history tab). */
  hasOverlayControls?: boolean;
  /** History mode: the selected run's id and owning workflow id. Forwarded to each card so its
   *  expanded body can load that service's transactions (#6009). */
  runId?: string;
  runWorkflowId?: string;
  /** Flatten the hierarchy: full-width cards, no indent, no connecting lines. Persisted toggle. */
  flat?: boolean;
}>();

const emit = defineEmits<{
  nodeClick: [event: { node: { id: string; data: any } }];
  addService: [parentNodeId: string];
  runService: [nodeId: string];
  toggleDisabled: [nodeId: string];
  deleteService: [nodeId: string];
  renameService: [nodeId: string];
  duplicateService: [nodeId: string];
  editParents: [nodeId: string];
  toggleViewMode: [];
  expandAll: [];
  collapseAll: [];
  serviceUpdate: [nodeId: string, service: Service];
  serviceSave: [nodeId: string];
  serviceDiscard: [nodeId: string];
  toggleComponent: [nodeId: string, isComponent: boolean];
  cardExpansionChanged: [nodeId: string, expanded: boolean];
}>();

// Resolve the Service for a given node id from the parent-supplied services prop.
function getServiceForNode(nodeId: string): Service | null {
  return props.services?.find(s => s.id === nodeId) ?? null;
}

function isNodeDirty(nodeId: string): boolean {
  return props.dirtyServiceIds?.has(nodeId) ?? false;
}

// Use the stacked workflow composable
const {
  sortedNodes,
  expandedNodes,
  stackContainer,
  getNodeDepth,
  getChildren,
  getParents,
  hasMultipleParents,
  getParentCount,
  toggleExpand,
  expandAll,
  collapseAll,
} = useStackedWorkflow(
  computed(() => props.nodes),
  computed(() => props.edges)
);

// Expose expansion helpers for deep-linking (parent component can call
// expandNode after mounting to programmatically expand a specific service).
// Opens the card and leaves the page where it is. The reader clicked something they can already see, so
// moving the page under them only costs them their place.
function expandNode(nodeId: string) {
  if (!expandedNodes.value.has(nodeId)) {
    toggleExpand(nodeId);
  }
}

function collapseNode(nodeId: string) {
  if (expandedNodes.value.has(nodeId)) {
    toggleExpand(nodeId);
  }
}

// Use the hierarchy lines composable for SVG-based connections
const { lines, junctions, svgWidth, svgHeight, recalculateLines } = useHierarchyLines(
  stackContainer,
  computed(() => props.nodes),
  computed(() => props.edges),
  computed(() => props.runningNodes),
  computed(() => props.liveRunData),
  computed(() => props.effectiveStatusByService)
);

// The line geometry is measured from each card's rendered left edge. While flattened the cards sit at
// zero indent and the SVG is hidden, but a data change (e.g. a live run frame) can still recompute the
// lines against that flattened geometry. Recalculate once on the way back to the indented layout so the
// re-shown rails match the re-indented cards — the container's own size doesn't change with card
// margins, so the ResizeObserver won't catch this on its own.
watch(
  () => props.flat,
  (flat) => {
    if (!flat) nextTick(recalculateLines);
  }
);

// Get parent nodes for display
function getParentNodes(nodeId: string): any[] {
  const parentIds = getParents(nodeId);
  return parentIds
    .map(parentId => props.nodes.find(n => n.id === parentId))
    .filter(Boolean);
}

// Handle expand/collapse — also surfaces the new expansion state to the parent so
// callers (e.g., project history tab) can sync URL state.
function handleToggleExpand(nodeId: string) {
  toggleExpand(nodeId);
  emit('cardExpansionChanged', nodeId, expandedNodes.value.has(nodeId));
}

// Handle expand/collapse all
function handleExpandAll() {
  expandAll();
  emit('expandAll');
}

function handleCollapseAll() {
  collapseAll();
  emit('collapseAll');
}

// Handle node click - emit to parent
function handleNodeClick(nodeId: string, nodeData: any) {
  emit('nodeClick', {
    node: { id: nodeId, data: nodeData }
  });
}

// Handle add service
function handleAddService(parentNodeId: string) {
  emit('addService', parentNodeId);
}

// Handle run service
function handleRunService(nodeId: string) {
  emit('runService', nodeId);
}

// Handle toggle disabled
function handleToggleDisabled(nodeId: string) {
  emit('toggleDisabled', nodeId);
}

// Handle delete service
function handleDeleteService(nodeId: string) {
  emit('deleteService', nodeId);
}

// Handle rename service
function handleRenameService(nodeId: string) {
  emit('renameService', nodeId);
}

// Handle duplicate service
function handleDuplicateService(nodeId: string) {
  emit('duplicateService', nodeId);
}

// Handle edit parent services (relationship editing)
function handleEditParents(nodeId: string) {
  emit('editParents', nodeId);
}

// Expose methods to parent component via ref
defineExpose({
  handleExpandAll,
  handleCollapseAll,
  expandNode,
  collapseNode,
});

// Check if there are any nodes
const hasNodes = computed(() => props.nodes.length > 0);

// Whether the hierarchy lines should carry status colour right now. In history mode the parent supplies
// `runColoringActive` — true through the run AND the brief post-finish hold, then false — so the stacked
// view colours, holds, and releases to grey in lockstep with the canvas. When no flag is supplied
// (editor/live builder), fall back to the local "any node running/pending" check.
const runInProgress = computed(() => {
  if (props.runColoringActive !== undefined) return props.runColoringActive;
  return props.nodes.some((n) => {
    const s = n.data?.run?.status;
    return s === "Running" || s === "Pending";
  });
});

// Count stats
const totalServices = computed(() => props.nodes.length);
const enabledServices = computed(() =>
  props.nodes.filter(n => !n.data.disabled).length
);
const disabledServices = computed(() =>
  props.nodes.filter(n => n.data.disabled).length
);

function lineStatusClass(line: LineConnection): string {
  // Grey structural lines unless the run is live — then colour by segment outcome.
  if (!runInProgress.value) return 'line-idle';
  switch (line.status) {
    case 'Running':   return 'line-running';
    case 'Succeeded': return 'line-succeeded';
    case 'Failed':    return 'line-failed';
    case 'Warning':   return 'line-warning';
    default: return 'line-idle';
  }
}

function lineStrokeWidth(line: LineConnection, outline = false): number {
  // Keep the thin (1px) weight everywhere; colour alone signals progress on live runs.
  return outline ? 3 : 1;
}

function lineDashArray(line: LineConnection): string | undefined {
  if (runInProgress.value && line.status === 'Running') return '6,3';
  return undefined;
}

// Calculate shortened path for animated dots to stop before arrowhead
function getShortenedPath(line: any) {
  const stopDistance = 12; // Stop 12px before the end to avoid arrowhead overlap
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length <= stopDistance) {
    return `M ${line.x1} ${line.y1} L ${line.x1} ${line.y1}`; // Don't animate if line is too short
  }

  const ratio = (length - stopDistance) / length;
  const newX2 = line.x1 + dx * ratio;
  const newY2 = line.y1 + dy * ratio;

  return `M ${line.x1} ${line.y1} L ${newX2} ${newY2}`;
}

</script>

<template>
  <div class="stacked-workflow-view" :class="{ 'has-overlay-controls': hasOverlayControls }">
    <!-- Service stack container -->
    <div class="service-stack" ref="stackContainer">
      <!-- SVG overlay for hierarchy lines (hidden when flattened — no indent to connect) -->
      <svg
        v-if="hasNodes && !flat"
        class="hierarchy-lines-svg"
        :width="svgWidth"
        :height="svgHeight"
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="2.5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,5 L7,2.5 z" fill="context-stroke" />
          </marker>
        </defs>

        <!-- Draw all horizontal lines first -->
        <g>
          <line
            v-for="(line, index) in lines.filter(l => l.type === 'horizontal')"
            :key="`h-outline-${index}`"
            :x1="line.x1"
            :y1="line.y1"
            :x2="line.x2"
            :y2="line.y2"
            stroke="var(--bs-body-bg, white)"
            :stroke-width="lineStrokeWidth(line, true)"
            stroke-linecap="round"
            :stroke-dasharray="lineDashArray(line)"
          />
          <line
            v-for="(line, index) in lines.filter(l => l.type === 'horizontal')"
            :key="`h-line-${index}`"
            :class="lineStatusClass(line)"
            :x1="line.x1"
            :y1="line.y1"
            :x2="line.x2"
            :y2="line.y2"
            :stroke-width="lineStrokeWidth(line)"
            stroke-linecap="round"
            :stroke-dasharray="lineDashArray(line)"
            :marker-end="runInProgress && line.status ? 'url(#arrowhead)' : ''"
          />
        </g>

        <!-- Draw all vertical lines second -->
        <g>
          <line
            v-for="(line, index) in lines.filter(l => l.type === 'vertical')"
            :key="`v-outline-${index}`"
            :x1="line.x1"
            :y1="line.y1 + 2"
            :x2="line.x2"
            :y2="line.y2"
            stroke="var(--bs-body-bg, white)"
            :stroke-width="lineStrokeWidth(line, true)"
            stroke-linecap="round"
            :stroke-dasharray="lineDashArray(line)"
          />
          <line
            v-for="(line, index) in lines.filter(l => l.type === 'vertical')"
            :key="`v-line-${index}`"
            :class="lineStatusClass(line)"
            :x1="line.x1"
            :y1="line.y1 + 2"
            :x2="line.x2"
            :y2="line.y2"
            :stroke-width="lineStrokeWidth(line)"
            stroke-linecap="round"
            :stroke-dasharray="lineDashArray(line)"
          />
        </g>

        <!-- Junction dots on top to clarify connection points -->
        <circle
          v-for="(junction, index) in junctions"
          :key="`junction-${index}`"
          :cx="junction.x"
          :cy="junction.y"
          r="3"
          class="junction-dot"
        />

        <!-- Animated dots for running lines (only while the run is in progress) -->
        <g v-if="runInProgress">
          <circle
            v-for="(line, index) in lines.filter(l => l.status === 'Running')"
            :key="`dot-${index}`"
            r="4"
            class="line-dot"
            :class="`line-dot-${index}`"
          >
            <animateMotion
              :dur="line.type === 'vertical' ? '2s' : '1.5s'"
              repeatCount="indefinite"
              :path="getShortenedPath(line)"
            />
          </circle>
        </g>
      </svg>

      <div v-if="!hasNodes" class="empty-state text-center py-5">
        <i class="bi bi-inbox display-1 text-muted mb-3"></i>
        <h5 class="text-muted">No Services Yet</h5>
        <p class="text-muted">
          Add your first service to start building your workflow
        </p>
      </div>

      <div v-else class="service-list">
        <StackedServiceCard
          v-for="node in sortedNodes"
          :key="node.id"
          :node="node"
          :depth="getNodeDepth(node.id)"
          :is-expanded="expandedNodes.has(node.id)"
          :children="getChildren(node.id)"
          :has-multiple-parents="hasMultipleParents(node.id)"
          :parent-count="getParentCount(node.id)"
          :parent-nodes="getParentNodes(node.id)"
          :service="getServiceForNode(node.id)"
          :is-dirty="isNodeDirty(node.id)"
          :run-id="runId"
          :run-workflow-id="runWorkflowId"
          :flat="flat"
          @toggle-expand="handleToggleExpand(node.id)"
          @node-click="handleNodeClick"
          @add-service="handleAddService"
          @run-service="handleRunService"
          @toggle-disabled="handleToggleDisabled"
          @delete-service="handleDeleteService"
          @rename-service="handleRenameService"
          @duplicate-service="handleDuplicateService"
          @edit-parents="handleEditParents"
          @service-update="(id, svc) => emit('serviceUpdate', id, svc)"
          @service-save="(id) => emit('serviceSave', id)"
          @service-discard="(id) => emit('serviceDiscard', id)"
          @toggle-component="(id, v) => emit('toggleComponent', id, v)"
        />
      </div>
    </div>

    <!-- Help text for mobile -->
    <div class="help-text text-center text-muted small mt-4 mb-3 d-md-none">
      <i class="bi bi-info-circle me-1"></i>
      Tap a service to view details, or use the menu for actions
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stacked-workflow-view {
  height: 100%;
  display: flex;
  flex-direction: column;
  //background: var(--bs-gray-100);
}

//[data-bs-theme='dark'] .stacked-workflow-view {
//  background: #1a1a1a;
//}

.stacked-header {
  flex-shrink: 0;
}

.service-stack {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  /* 1rem top padding matches the overlay controls' `top: 1rem` (.run-canvas-controls), so the first
     service panel lines up with the top of the list/canvas toggle button. */
  /* Left padding just clears the root lead-in stub + its dot (~12px stub + dot radius).
     The rails now hug the cards (bottom-exit hierarchy), so the old 3rem gutter left a
     dead band to the left of the root dot. */
  padding: 1rem 1.5rem 1rem 1rem;
  scroll-behavior: smooth;
  position: relative;
}

/* Reserve room on the right so the floating control cluster doesn't overlap the cards. */
.has-overlay-controls .service-stack {
  padding-right: 4.5rem;
}

.hierarchy-lines-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
}

/* Custom scrollbar for better UX */
.service-stack::-webkit-scrollbar {
  width: 8px;
}

//.service-stack::-webkit-scrollbar-track {
//  background: var(--bs-gray-200);
//}

//[data-bs-theme='dark'] .service-stack::-webkit-scrollbar-track {
//  background: #2a2a2a;
//}

//.service-stack::-webkit-scrollbar-thumb {
//  background: var(--bs-gray-400);
//  border-radius: 4px;
//}
//
//.service-stack::-webkit-scrollbar-thumb:hover {
//  background: var(--bs-gray-500);
//}

//[data-bs-theme='dark'] .service-stack::-webkit-scrollbar-thumb {
//  background: #404040;
//}
//
//[data-bs-theme='dark'] .service-stack::-webkit-scrollbar-thumb:hover {
//  background: #505050;
//}

.service-list {
  width: 100%;
}

.empty-state {
  padding: 3rem 1rem;
}

.stat {
  white-space: nowrap;
}

/* Status-based line colours — Bootstrap CSS vars for theme/dark-mode awareness. The stroke transition
   eases a line back to grey when the run finishes (after the shared hold), matching the canvas fade.
   It only animates `stroke`, so the running line's dashFlow animation is unaffected. */
.hierarchy-lines-svg line {
  transition: stroke 0.6s ease;
}
.line-running     { stroke: var(--bs-primary);        animation: dashFlow 1.5s linear infinite; }
.line-succeeded   { stroke: var(--bs-success); }
.line-failed      { stroke: var(--bs-danger); }
.line-warning     { stroke: var(--bs-warning); }
.line-idle        { stroke: var(--bs-border-color); }
.line-dot         { fill: var(--bs-primary); }
.junction-dot     { fill: var(--bs-warning); stroke: var(--bs-body-bg); stroke-width: 1.5; }

@keyframes dashFlow {
  0%   { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -18; /* -(6 + 3) * 2 for smooth loop */ }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  /* Both selectors so this matches the specificity of `.has-overlay-controls .service-stack`
     (desktop right padding) — a media query adds none, so a bare `.service-stack` would lose. */
  .service-stack,
  .has-overlay-controls .service-stack {
    padding: 0.375rem calc(1rem + 36px) 0.5rem calc(0.25rem + 11px);
  }
}
</style>
